import React from 'react';
import { typography, textColors, type HeadingLevel } from '../styles/typography';

interface HeadingProps {
  /**
   * Heading level (1-5) determines the HTML tag (h1-h5)
   * IMPORTANT: Each page must have exactly ONE h1
   */
  level: HeadingLevel;

  /**
   * Content to display
   */
  children: React.ReactNode;

  /**
   * Additional CSS classes
   */
  className?: string;

  /**
   * Text color variant
   * @default 'primary'
   */
  color?: keyof typeof textColors;

  /**
   * HTML id attribute (useful for anchor links)
   */
  id?: string;

  /**
   * Whether to center align the text
   * @default false
   */
  center?: boolean;

  /**
   * Maximum width for readability
   */
  maxWidth?: 'none' | 'optimal' | 'wide' | 'full';
}

/**
 * Heading Component
 *
 * Standardized heading component that enforces typography hierarchy
 * and accessibility best practices.
 *
 * Usage:
 * ```tsx
 * // Page title (ONE per page)
 * <Heading level={1}>Dashboard</Heading>
 *
 * // Section title
 * <Heading level={2}>Recent Activity</Heading>
 *
 * // Subsection or card title
 * <Heading level={3}>User Statistics</Heading>
 * ```
 *
 * Accessibility:
 * - Enforces semantic HTML heading hierarchy (h1-h5)
 * - Supports proper document structure for screen readers
 * - Maintains WCAG 2.1 Level AA compliance
 *
 * @see typography.ts for typography scale definition
 */
export function Heading({
  level,
  children,
  className = '',
  color = 'primary',
  id,
  center = false,
  maxWidth = 'none',
}: HeadingProps) {
  // Get the appropriate HTML tag
  const Tag = `h${level}` as const;

  // Get typography styles for this heading level
  const typographyStyles = typography.heading[level];

  // Get text color
  const colorStyles = textColors[color];

  // Center alignment
  const alignmentStyles = center ? 'text-center' : '';

  // Max width for readability
  const maxWidthStyles = {
    none: '',
    optimal: 'max-w-2xl',
    wide: 'max-w-4xl',
    full: 'max-w-full',
  }[maxWidth];

  // Combine all styles
  const combinedStyles = [typographyStyles, colorStyles, alignmentStyles, maxWidthStyles, className]
    .filter(Boolean)
    .join(' ');

  return (
    <Tag id={id} className={combinedStyles}>
      {children}
    </Tag>
  );
}

/**
 * Convenience components for each heading level
 * Use these when the level is known at compile time
 */
export function H1(props: Omit<HeadingProps, 'level'>) {
  return <Heading {...props} level={1} />;
}

export function H2(props: Omit<HeadingProps, 'level'>) {
  return <Heading {...props} level={2} />;
}

export function H3(props: Omit<HeadingProps, 'level'>) {
  return <Heading {...props} level={3} />;
}

export function H4(props: Omit<HeadingProps, 'level'>) {
  return <Heading {...props} level={4} />;
}

export function H5(props: Omit<HeadingProps, 'level'>) {
  return <Heading {...props} level={5} />;
}
